import { useMemo } from "react"
import { useQuery } from "@rocicorp/zero/react"

import type { CustomView } from "@productlane/zero/schema"
import { queries } from "@productlane/zero/queries"

import type { InboxState, ThreadAnchor } from "@/lib/states/global-state"
import type { AssignedSubTab } from "@/screens/app/inbox/helpers/assigned-sub-tab"
import { useTimeTick } from "@/lib/hooks/use-time-tick"
import { useSortState } from "@/lib/states/global-state"
import { mergeThreadFilters } from "../filter/merge-thread-filters"
import { useThreadsFilterState } from "../filter/threads-filter-state"

const SLA_TICK_INTERVAL_MS = 5 * 60 * 1000

export const resolveSortColumn = (
	tab: InboxState,
	sortColumn: "lastMessageAt" | "painLevel" | "snoozedUntil",
) => {
	if (tab === "done" || tab === "my-done") {
		return "lastMessageAt"
	}
	if (sortColumn === "lastMessageAt" || sortColumn === "snoozedUntil") {
		if (tab === "snoozed" || tab === "my-snoozed") return "snoozedUntil"
		return "lastMessageAt"
	}
	return sortColumn
}

export const useThreadsForList = ({
	tab,
	anchor,
	pageSize,
	overrideFilters,
	stateScope,
}: {
	tab: InboxState
	anchor: ThreadAnchor
	pageSize: number
	overrideFilters?: CustomView["filters"]
	stateScope?: AssignedSubTab
}) => {
	const { sortByQueryColumn, sortByQueryColumnOrder } = useSortState()
	const sortColumn = resolveSortColumn(tab, sortByQueryColumn)
	const liveFilters = useThreadsFilterState()

	const filters = useMemo(
		() => mergeThreadFilters(overrideFilters, liveFilters),
		[overrideFilters, liveFilters],
	)

	const anchoredSortOrder =
		anchor.direction === "forward"
			? sortByQueryColumnOrder
			: sortByQueryColumnOrder === "asc"
				? "desc"
				: "asc"

	const tick = useTimeTick(SLA_TICK_INTERVAL_MS)
	const nowMs = useMemo(
		() =>
			tab === "sla-breached"
				? Math.floor(Date.now() / SLA_TICK_INTERVAL_MS) * SLA_TICK_INTERVAL_MS
				: undefined,
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[tab, tick],
	)

	const [threads, queryResult] = useQuery(
		queries.threadSearch({
			tab,
			filters,
			stateScope,
			sortColumn,
			sortOrder: anchoredSortOrder,
			limit: pageSize,
			nowMs,
			startAfter: anchor.cursor
				? {
						createdAt: anchor.cursor.createdAt,
						lastStateChangeAt: anchor.cursor.lastStateChangeAt,
						lastMessageAt: anchor.cursor.lastMessageAt,
						painLevel: anchor.cursor.painLevel,
						snoozedUntil: anchor.cursor.snoozedUntil,
						id: anchor.cursor?.id,
					}
				: undefined,
		}),
	)

	return {
		threads,
		isLoading: queryResult.type !== "complete",
	}
}
